import { FILTERING_THROTTLE_INTERVAL } from 'app-constants';
import { action, computed, observable, observe, runInAction } from 'mobx';
import {
	bookingsApi,
	PastBookingsData,
	PostBookingParams,
	LeaveBookingFeedbackRequest,
	LeaveBookingFeedbackData,
} from 'AppClient/api/Bookings';
import { RootStore } from 'AppClient/stores';
import { find, isEmpty, isEqual, once, debounce } from 'lodash-es';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { datetimeWithTimeZone, roundNumber } from 'utils/converters';

export class PastBookingsStore {
	@observable private _pastBookings: PastBookingsData[] = [];
	@observable private _totalCount: number = 0;
	@observable private _fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private _isData: boolean = false;
	@observable isShownBookingFeedbackModal: boolean = false;
	@observable bookingFeedbackModalData = {} as LeaveBookingFeedbackData;
	@observable
	private _requestParams: PostBookingParams = {
		'order.OrderByAscending': false,
		'order.OrderColumn': 'PickUpDateTime',
		'page.NumberOfItemsOnPage': 10,
		'page.PageNumber': 1,
	};

	constructor(private rootStore: RootStore) {
		observe(
			this,
			'requestParams',
			debounce(({ newValue, oldValue }) => {
				if (!isEqual(newValue, oldValue)) {
					this.receivePastBookings();
				}
			}, FILTERING_THROTTLE_INTERVAL)
		);
	}

	@computed
	get requestParams() {
		return this._requestParams;
	}

	@computed
	get isData() {
		return this._isData;
	}

	@computed
	get isIdle(): boolean {
		return this._fetchStatus === FetchStatus.IDLE;
	}

	@computed
	get isPending(): boolean {
		return this._fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get isLoadingPast() {
		return this.isIdle || this.isPending;
	}

	@computed
	get isSuccess(): boolean {
		return this._fetchStatus === FetchStatus.SUCCESS;
	}

	@computed
	get getTotalCount() {
		return this._totalCount;
	}

	@computed
	get pastBookingsData() {
		return this._pastBookings;
	}

	@action.bound
	toggleModal() {
		this.isShownBookingFeedbackModal = !this.isShownBookingFeedbackModal;
	}

	@action.bound
	bookingFeedback(bookingId: number) {
		const pastBooking = this.findPastBookingById(bookingId);

		if (pastBooking) {
			this.bookingFeedbackModalData.bookingId = pastBooking.bookingId;
			this.bookingFeedbackModalData.bookingNumber = pastBooking.bookingNumber;
			this.bookingFeedbackModalData.pickUpDateTime = pastBooking.pickUpDateTime;
			this.bookingFeedbackModalData.addresses = pastBooking.bookingAddresses;
			this.bookingFeedbackModalData.servicePartnerName = pastBooking.servicePartnerName;

			this.toggleModal();
		}
	}

	@action
	private setIsData = once((isData: boolean) => {
		this._isData = isData;
	});

	@action
	public setIsDataFromOutside = (isData: boolean) => {
		this._isData = isData;
	};

	@action.bound
	setRequestParams(params: PostBookingParams) {
		this._requestParams = {
			...params,
		};
	}

	@action.bound
	async receivePastBookings() {
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const response = await bookingsApi.getPastBookings(this.requestParams);
			const { error } = response.data;
			if (error) {
				this.rootStore.alertStore.setErrorAlert(error);
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}
			const {
				result: { totalCount = 0, entities },
			} = response.data;
			if (entities) {
				runInAction(() => {
					this.setIsData(!isEmpty(entities));
					this._fetchStatus = FetchStatus.SUCCESS;
					this._totalCount = totalCount;
					this._pastBookings = this.convertBookingList(entities);
				});
			}
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}

	convertBookingList = (bookingList: PastBookingsData[]): PastBookingsData[] => {
		const clone = [...bookingList];
		return clone.map(book => {
			const { customerCurrencySymbol, totalPriceInCustomerCurrency } = book;
			book.pickUpDateTime = datetimeWithTimeZone(book.pickUpDateTime);
			book.totalPriceInCustomerCurrency = `${customerCurrencySymbol} ${roundNumber(
				totalPriceInCustomerCurrency
			)}`;
			return book;
		});
	};

	@action.bound
	findPastBookingById(bookingId: number): PastBookingsData | undefined {
		return find(this.pastBookingsData, { bookingId });
	}

	@action.bound
	async leaveBookingFeedback(feedbackData: LeaveBookingFeedbackRequest) {
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const response = await bookingsApi.leaveBookingFeedback(feedbackData);
			const { error } = response.data;
			if (error) {
				this.rootStore.alertStore.setErrorAlert(error);
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			} else {
				await this.receivePastBookings();
				this._fetchStatus = FetchStatus.SUCCESS;
				this.toggleModal();
			}
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}
}
